var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { Component, Fragment } from 'react';
import { AboutBox, Button, Field, Footer, InlineImage, Input, InstructionsForUse, Link, localizedLabelShape, ManifestDependencies, Modal, ToastNotification } from '@philips-internal/oswald-ui-components';
import { AutocompleteType, ButtonTheme, ButtonType, InputType, Key, ModalType, Size, ThemeTonalRange, ToastType } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, extendComponent, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { PromiseEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { AuthenticationService, HistoryService, MessageService, MultiFactorAuthenticationRequiredError, OpenIDConnectService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, deprecationWarningMessage, enumValues, getSlot, isContentfulString, isEnumValid, isFunction, isMobileResolution } from '@philips-internal/oswald-ui-utils';
import { MultiFactorAuthenticationModalService } from '../../services/multi-factor-authentication-service.js';
import { LoginAPI } from './login-api.js';
import { LoginMode, LoginSlots, LoginTopMode } from './login-constants.js';
import './login.css';
const acceptedTypes = [
    ComponentType.DATA_DISPLAY,
    ComponentType.LAYOUT,
    ComponentType.NAVIGATION,
];
const LoginPrimaryCustomContainer = {
    acceptedTypes,
    name: LoginSlots.PRIMARY,
};
const LoginSecondaryCustomContainer = {
    acceptedTypes,
    name: LoginSlots.SECONDARY,
};
let Login = class Login extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            password: '',
            showAboutBox: false,
            showDependencies: false,
            showInstructionsForUse: false,
            username: '',
        };
        this.onPasswordChange = (event) => {
            const { value } = event;
            this.setState({ password: value });
        };
        this.onUsernameChange = (event) => {
            const { value } = event;
            this.setState({ username: value });
        };
        this.redirectToHomePage = () => {
            const queryParams = HistoryService.getCurrentRouteQueryParams();
            const currentState = OpenIDConnectService.getState();
            const isValidStateParam = currentState && OpenIDConnectService.validateState(queryParams?.state);
            if (!isValidStateParam || !isContentfulString(currentState.redirectPath)) {
                return AuthenticationService.redirectToHome();
            }
            HistoryService.push(currentState.redirectPath);
        };
        this.onLoginSuccess = (response) => {
            const { onSuccess } = this.props;
            let defaultPrevented = false;
            if (isFunction(onSuccess)) {
                const event = new PromiseEvent({
                    preventDefault: () => defaultPrevented = true,
                    response,
                });
                onSuccess(event);
            }
            if (!defaultPrevented) {
                this.redirectToHomePage();
            }
        };
        this.onLoginError = (error) => {
            const { onCatch } = this.props;
            if (error instanceof MultiFactorAuthenticationRequiredError) {
                this.callMultiFactorAuthentication(error.jwt);
                return;
            }
            if (isFunction(onCatch)) {
                const event = new PromiseEvent({
                    response: error,
                });
                // @ts-ignore: For some reason, The PromiseEvent expected Error doesn't match the default one.
                onCatch(event);
            }
        };
        this.onLoginClick = () => {
            this.login();
        };
        this.onForgotPasswordClick = () => {
            AuthenticationService.redirectToForgotPassword();
        };
        this.onKeyDown = (event) => {
            if (event.key !== Key.ENTER) {
                return;
            }
            this.login();
        };
        this.toggleAboutBox = () => {
            this.setState(({ showAboutBox }) => ({ showAboutBox: !showAboutBox }));
        };
        this.toggleDependencies = () => {
            this.setState(({ showDependencies }) => ({ showDependencies: !showDependencies }));
        };
        this.toggleInstructionsForUse = () => {
            this.setState(({ showInstructionsForUse }) => ({ showInstructionsForUse: !showInstructionsForUse }));
        };
    }
    login() {
        const { password, username } = this.state;
        AuthenticationService.login({ password, username })
            .then((response) => this.onLoginSuccess(response))
            .catch((error) => this.onLoginError(error));
    }
    renderAboutBoxModal() {
        const { aboutBoxModalTheme, aboutBoxProps, localizedLabel } = this.props;
        const { showAboutBox } = this.state;
        if (!showAboutBox) {
            return null;
        }
        if (localizedLabel) {
            deprecationWarningMessage({
                deadlineVersion: '7.0.0',
                property: true,
                replaced: 'localizedLabel',
                scope: 'Login',
                startingVersion: '6.6.0',
            });
        }
        const modalType = isMobileResolution() ? ModalType.FULLSCREEN : ModalType.DEFAULT;
        const aboutBoxProperties = { ...aboutBoxProps, localizedLabel: localizedLabel ?? aboutBoxProps?.localizedLabel };
        return (React.createElement(Modal, { className: "c-login-modal", onClose: this.toggleAboutBox, theme: aboutBoxModalTheme, type: modalType },
            React.createElement(AboutBox, { ...aboutBoxProperties })));
    }
    renderManifestDependenciesModal() {
        const { applicationName, dependenciesModalTheme } = this.props;
        const { showDependencies } = this.state;
        if (!showDependencies) {
            return null;
        }
        const modalType = isMobileResolution() ? ModalType.FULLSCREEN : ModalType.DEFAULT;
        return (React.createElement(Modal, { className: "c-login-modal", onClose: this.toggleDependencies, theme: dependenciesModalTheme, type: modalType },
            React.createElement(ManifestDependencies, { applicationName: applicationName })));
    }
    renderInstructionsForUseModal() {
        const { showInstructionsForUse } = this.state;
        if (!showInstructionsForUse) {
            return null;
        }
        const { instructionsForUseModalTheme, instructionsForUseProps } = this.props;
        const modalType = isMobileResolution() ? ModalType.FULLSCREEN : ModalType.DEFAULT;
        return (React.createElement(Modal, { className: "c-login-modal", onClose: this.toggleInstructionsForUse, theme: instructionsForUseModalTheme, type: modalType },
            React.createElement(InstructionsForUse, { ...instructionsForUseProps, productName: instructionsForUseProps?.productName ?? '' })));
    }
    callMultiFactorAuthentication(token) {
        const { closeableMultiFactorAuthentication } = this.props;
        MultiFactorAuthenticationModalService.open({ closeable: Boolean(closeableMultiFactorAuthentication), token })
            .then((response) => this.onLoginSuccess(response.token))
            .catch((error) => this.onLoginError(error));
    }
    renderForm() {
        const { passwordExpression, usernameExpression } = this.props;
        const { password, username } = this.state;
        const data = { password, username };
        return (React.createElement("form", { className: "c-login__form" },
            React.createElement(Field, { className: "c-login__field", data: data, entry: "username", hideLabel: true, label: usernameExpression, onChange: this.onUsernameChange, onKeyDown: this.onKeyDown, uuid: "fake-uuid", width: 12 },
                React.createElement(Input, { autoComplete: AutocompleteType.USERNAME })),
            React.createElement(Field, { className: "c-login__field", data: data, entry: "password", hideLabel: true, label: passwordExpression, onChange: this.onPasswordChange, onKeyDown: this.onKeyDown, uuid: "fake-uuid", width: 12 },
                React.createElement(Input, { autoComplete: AutocompleteType.CURRENT_PASSWORD, type: InputType.PASSWORD }))));
    }
    renderCustom(container) {
        const { children, uuid } = this.props;
        let content = getSlot(children, container.name);
        if (container.name === LoginSlots.PRIMARY && !content.length) {
            const missingSlotContent = Boolean(children.length) && !getSlot(children, LoginSlots.SECONDARY).length;
            if (missingSlotContent) {
                deprecationWarningMessage({
                    deadlineVersion: '7.0.0',
                    feature: true,
                    replaced: 'slotless children',
                    scope: 'Login',
                    startingVersion: '6.8.0',
                });
                content = children;
            }
        }
        return (React.createElement(Container, { acceptedTypes: container.acceptedTypes, className: "c-login__custom", "data-area-name": container.name, slot: container.name, uuid: uuid }, content));
    }
    renderDependencies() {
        const { dependenciesExpression, hideDependencies } = this.props;
        if (hideDependencies) {
            return null;
        }
        return (React.createElement("div", { className: "c-login__text c-login__dependencies", onClick: this.toggleDependencies },
            React.createElement("span", { className: "c-login__dependencies-text" }, MessageService.translate(dependenciesExpression))));
    }
    renderFooter() {
        const { footerExpression, hideFooter } = this.props;
        if (hideFooter) {
            return null;
        }
        return (React.createElement(Footer, { textExpression: footerExpression }));
    }
    renderVersion() {
        const { hideVersion, versionExpression } = this.props;
        if (hideVersion) {
            return null;
        }
        return (React.createElement("div", { className: "c-login__text c-login__version", onClick: this.toggleAboutBox },
            React.createElement("span", { className: "c-login__version-text" }, MessageService.translate(versionExpression))));
    }
    renderInstructionsForUse() {
        const { hideInstructionOfUse, instructionsForUseExpression } = this.props;
        if (hideInstructionOfUse) {
            return null;
        }
        return (React.createElement("div", { className: "c-login__text c-login__instructions-for-use", onClick: this.toggleInstructionsForUse },
            React.createElement("span", { className: "c-login__instructions-for-use-text" }, MessageService.translate(instructionsForUseExpression))));
    }
    renderLoginButton() {
        const { loginExpression } = this.props;
        return (React.createElement(Button, { className: "c-login__action", onClick: this.onLoginClick, size: Size.SMALL, theme: ButtonTheme.PRIMARY, type: ButtonType.SUBMIT, value: loginExpression }));
    }
    renderForgotPassword() {
        const { forgotPasswordExpression, hideForgotPassword } = this.props;
        if (hideForgotPassword) {
            return null;
        }
        return (React.createElement(Link, { className: "c-login__forgot-password", onClick: this.onForgotPasswordClick, size: Size.LARGE, value: forgotPasswordExpression }));
    }
    renderNotification() {
        const { notification } = this.state;
        if (!notification) {
            return null;
        }
        return (React.createElement(ToastNotification, { className: "c-login__notification", dismissible: false, showIcon: true, type: ToastType.BANNER, ...notification }));
    }
    renderLoginTop() {
        const { applicationName, loginTopMode, logo } = this.props;
        if (loginTopMode === LoginTopMode.TEXT) {
            return this.renderSignInMessage();
        }
        return (React.createElement(Fragment, null,
            React.createElement(InlineImage, { className: "c-login__logo", imageAlt: "Application logo", src: logo }),
            React.createElement("div", { className: "c-login__text c-login__name" }, MessageService.translate(applicationName))));
    }
    renderSignInMessage() {
        const { signInTextExpression, signInTitleExpression } = this.props;
        return (React.createElement("div", { className: "c-login__top" },
            React.createElement("div", { className: "c-login__sign-in-title" }, MessageService.translate(signInTitleExpression)),
            React.createElement("div", { className: "c-login__sign-in-message" }, MessageService.translate(signInTextExpression))));
    }
    renderSeparator(hide) {
        if (hide) {
            return null;
        }
        return (React.createElement("div", { className: "c-login__bottom-link-separator" }));
    }
    render() {
        const { hideDependencies, hideInstructionOfUse, hideVersion, mode } = this.props;
        const loginClassName = classNames({
            'c-login': true,
            [`c-login--${mode}`]: isEnumValid(LoginMode, mode),
        });
        const bottomLinksClassName = classNames({
            'c-login__bottom-links': true,
            [`c-login__bottom-links--has-separator`]: !hideDependencies && !hideVersion && !hideInstructionOfUse,
        });
        return (React.createElement("div", { className: loginClassName },
            React.createElement("div", { className: "c-login__main" },
                this.renderLoginTop(),
                this.renderForm(),
                this.renderLoginButton(),
                this.renderNotification(),
                this.renderForgotPassword()),
            this.renderCustom(LoginPrimaryCustomContainer),
            React.createElement("div", { className: "c-login__bottom" },
                React.createElement("div", { className: bottomLinksClassName },
                    this.renderVersion(),
                    this.renderSeparator(hideVersion || hideDependencies && hideInstructionOfUse),
                    this.renderDependencies(),
                    this.renderSeparator(hideInstructionOfUse || hideDependencies && hideVersion),
                    this.renderInstructionsForUse()),
                this.renderCustom(LoginSecondaryCustomContainer),
                this.renderFooter()),
            this.renderAboutBoxModal(),
            this.renderManifestDependenciesModal(),
            this.renderInstructionsForUseModal()));
    }
};
Login.propTypes = {
    aboutBoxModalTheme: oneOf([ThemeTonalRange.BRIGHT, ThemeTonalRange.ULTRA_LIGHT]),
    aboutBoxProps: extendComponent('AboutBox', [
        'companyAddress',
        'companyAddressIcon',
        'companyComplementaryAddress',
        'companyAddressOutlineIcon',
        'companyReference',
        'companyReferenceIcon',
        'localizedLabel',
        'logoIcon',
        'LOTIcon',
        'messageExpression',
        'productName',
        'productSubtitle',
        'quantityIcon',
        'REFIcon',
        'secondaryMessageExpression',
        'showQuantity',
        'UDIIcon',
        'versionExpression',
    ], [
        'productExpression',
    ]),
    applicationName: expression,
    closeableMultiFactorAuthentication: bool,
    dependenciesExpression: expression,
    dependenciesModalTheme: oneOf([ThemeTonalRange.BRIGHT, ThemeTonalRange.ULTRA_LIGHT]),
    forgotPasswordExpression: expression,
    hideDependencies: bool,
    hideFooter: bool,
    hideForgotPassword: bool,
    hideInstructionOfUse: bool,
    hideVersion: bool,
    instructionsForUseExpression: expression,
    instructionsForUseModalTheme: oneOf([ThemeTonalRange.BRIGHT, ThemeTonalRange.ULTRA_LIGHT]),
    instructionsForUseProps: extendComponent('InstructionsForUse', ['logoIcon', 'productName'], ['instructionsForUse']),
    localizedLabel: localizedLabelShape.notEnumerable(),
    loginExpression: expression,
    loginTopMode: oneOf(enumValues(LoginTopMode)),
    logo: string,
    mode: oneOf(enumValues(LoginMode)),
    onCatch: eventListener,
    onSuccess: eventListener,
    passwordExpression: expression,
    signInTextExpression: string,
    signInTitleExpression: string,
    usernameExpression: expression,
    uuid: string,
    versionExpression: expression,
};
Login.defaultProps = {
    aboutBoxModalTheme: ThemeTonalRange.BRIGHT,
    applicationName: 'Foundation Application',
    dependenciesExpression: 'tws.login.dependencies',
    dependenciesModalTheme: ThemeTonalRange.BRIGHT,
    forgotPasswordExpression: 'tws.login.forgot-password',
    hideDependencies: false,
    hideFooter: false,
    hideForgotPassword: false,
    hideInstructionOfUse: true,
    hideVersion: false,
    instructionsForUseExpression: 'tws.login.instruction-for-use',
    instructionsForUseModalTheme: ThemeTonalRange.BRIGHT,
    loginExpression: 'tws.login.login',
    loginTopMode: LoginTopMode.LOGO,
    logo: '/static/assets/philips-shield.svg',
    mode: LoginMode.FULLSCREEN,
    passwordExpression: 'tws.login.password',
    signInTextExpression: 'tws.login.signin-message',
    signInTitleExpression: 'tws.login.login',
    usernameExpression: 'tws.login.username',
    uuid: '',
    versionExpression: 'tws.login.version',
};
Login = __decorate([
    AwesomeComponent({ api: LoginAPI, containers: [LoginPrimaryCustomContainer, LoginSecondaryCustomContainer], type: ComponentType.LAYOUT })
], Login);
export { Login, };
//# sourceMappingURL=login.js.map